/**
 * Amortization schedule calculator.
 * Generates a standard amortization table with principal + interest breakdown.
 */

export interface AmortizationRow {
  period: number;
  dueDate: string;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}

export interface AmortizationInput {
  principal: number; // Loan amount
  annualRate: number; // Annual interest rate as percentage (e.g. 5.25)
  termMonths: number; // Number of months
  startDate?: string; // ISO date string, defaults to today
  frequency?: 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY' | 'QUARTERLY';
  repaymentType?: 'STANDARD' | 'INTEREST_ONLY' | 'CAPITALISED';
  summaryTotalsMode?: 'ROUNDED_SCHEDULE' | 'EXACT';
}

export interface AmortizationSummary {
  schedule: AmortizationRow[];
  totalPayment: number;
  totalInterest: number;
  totalPrincipal: number;
  monthlyPayment: number;
  balloonPayment?: number;
}

/**
 * Calculate amortization schedule supporting multiple repayment types.
 * STANDARD: Fixed-payment amortization using formula M = P * [r(1+r)^n] / [(1+r)^n - 1]
 * INTEREST_ONLY: Only interest payments during term, balloon payment of principal at end
 * CAPITALISED: No payments during term, single payment at end (principal + all accumulated interest)
 */
export function calculateAmortization(input: AmortizationInput): AmortizationSummary {
  const {
    principal,
    annualRate,
    termMonths,
    startDate,
    frequency = 'MONTHLY',
    repaymentType = 'STANDARD',
    summaryTotalsMode = 'ROUNDED_SCHEDULE',
  } = input;

  // Determine periods per year and total periods
  const periodsPerYear =
    frequency === 'WEEKLY'
      ? 52
      : frequency === 'BIWEEKLY'
        ? 26
        : frequency === 'QUARTERLY'
          ? 4
          : 12;

  const totalPeriods =
    frequency === 'MONTHLY'
      ? termMonths
      : frequency === 'QUARTERLY'
        ? Math.ceil(termMonths / 3)
        : frequency === 'BIWEEKLY'
          ? Math.ceil((termMonths * 26) / 12)
          : Math.ceil((termMonths * 52) / 12);

  // Periodic interest rate
  const periodicRate = annualRate / 100 / periodsPerYear;

  // Generate schedule based on repayment type
  const schedule: AmortizationRow[] = [];
  const start = startDate ? new Date(startDate) : new Date();
  let summaryTotalPayment: number | null = null;
  let summaryTotalInterest: number | null = null;

  if (repaymentType === 'INTEREST_ONLY') {
    // Interest-only payments, balloon at the end
    let balance = principal;
    const interestOnlyPayment = principal * periodicRate;

    for (let i = 1; i <= totalPeriods; i++) {
      const dueDate = new Date(start);
      if (frequency === 'MONTHLY') {
        dueDate.setMonth(dueDate.getMonth() + i);
      } else if (frequency === 'QUARTERLY') {
        dueDate.setMonth(dueDate.getMonth() + i * 3);
      } else if (frequency === 'BIWEEKLY') {
        dueDate.setDate(dueDate.getDate() + i * 14);
      } else {
        dueDate.setDate(dueDate.getDate() + i * 7);
      }

      const isLastPeriod = i === totalPeriods;
      const payment = isLastPeriod ? balance + interestOnlyPayment : interestOnlyPayment;
      const principalPayment = isLastPeriod ? balance : 0;
      const interestPayment = interestOnlyPayment;

      schedule.push({
        period: i,
        dueDate: dueDate.toISOString().split('T')[0],
        payment: round(payment),
        principal: round(principalPayment),
        interest: round(interestPayment),
        balance: isLastPeriod ? 0 : round(balance),
      });
    }
  } else if (repaymentType === 'CAPITALISED') {
    // No payments during term, single payment at end with compound interest
    let finalAccumulatedInterest = 0;
    for (let i = 1; i <= totalPeriods; i++) {
      const dueDate = new Date(start);
      if (frequency === 'MONTHLY') {
        dueDate.setMonth(dueDate.getMonth() + i);
      } else if (frequency === 'QUARTERLY') {
        dueDate.setMonth(dueDate.getMonth() + i * 3);
      } else if (frequency === 'BIWEEKLY') {
        dueDate.setDate(dueDate.getDate() + i * 14);
      } else {
        dueDate.setDate(dueDate.getDate() + i * 7);
      }

      const isLastPeriod = i === totalPeriods;
      // Compound from original principal to avoid double-compounding
      const accumulatedInterest = principal * Math.pow(1 + periodicRate, i) - principal;
      if (isLastPeriod) {
        finalAccumulatedInterest = accumulatedInterest;
      }
      const balance = principal + accumulatedInterest;
      const payment = isLastPeriod ? balance : 0;

      schedule.push({
        period: i,
        dueDate: dueDate.toISOString().split('T')[0],
        payment: round(payment),
        principal: isLastPeriod ? round(principal) : 0,
        interest: round(accumulatedInterest),
        balance: isLastPeriod ? 0 : round(balance),
      });
    }

    summaryTotalPayment = round(schedule[schedule.length - 1]?.payment ?? 0);
    summaryTotalInterest = round(finalAccumulatedInterest);
  } else {
    // STANDARD: Fixed-payment amortization
    let fixedPayment: number;
    let exactTotalPayment = 0;
    let exactTotalInterest = 0;
    if (periodicRate === 0) {
      fixedPayment = principal / totalPeriods;
    } else {
      const factor = Math.pow(1 + periodicRate, totalPeriods);
      fixedPayment = (principal * (periodicRate * factor)) / (factor - 1);
    }

    let balance = principal;

    for (let i = 1; i <= totalPeriods; i++) {
      const dueDate = new Date(start);
      if (frequency === 'MONTHLY') {
        dueDate.setMonth(dueDate.getMonth() + i);
      } else if (frequency === 'QUARTERLY') {
        dueDate.setMonth(dueDate.getMonth() + i * 3);
      } else if (frequency === 'BIWEEKLY') {
        dueDate.setDate(dueDate.getDate() + i * 14);
      } else {
        dueDate.setDate(dueDate.getDate() + i * 7);
      }

      const interestPayment = balance * periodicRate;
      let principalPayment = fixedPayment - interestPayment;
      let paymentAmount = fixedPayment;

      // Handle last payment rounding
      if (i === totalPeriods) {
        principalPayment = balance;
        paymentAmount = principalPayment + interestPayment;
      }

      balance -= principalPayment;
      if (balance < 0.01) balance = 0;
      exactTotalInterest += interestPayment;
      exactTotalPayment += paymentAmount;

      schedule.push({
        period: i,
        dueDate: dueDate.toISOString().split('T')[0],
        payment: round(paymentAmount),
        principal: round(principalPayment),
        interest: round(interestPayment),
        balance: round(balance),
      });
    }

    if (summaryTotalsMode === 'EXACT') {
      // Summary totals use exact amortization values, while schedule rows are rounded for display.
      // This avoids cumulative rounding drift in loan-level totals.
      summaryTotalPayment = round(exactTotalPayment);
      summaryTotalInterest = round(exactTotalInterest);
    }
  }

  const totalPayment = summaryTotalPayment ?? schedule.reduce((s, r) => s + r.payment, 0);
  const totalInterest = summaryTotalInterest ?? schedule.reduce((s, r) => s + r.interest, 0);
  const balloonPayment = repaymentType === 'INTEREST_ONLY' ? principal : undefined;

  return {
    schedule,
    totalPayment: round(totalPayment),
    totalInterest: round(totalInterest),
    totalPrincipal: round(principal),
    monthlyPayment: round(
      frequency === 'MONTHLY' ? schedule[0]?.payment || 0 : totalPayment / termMonths,
    ),
    balloonPayment: balloonPayment ? round(balloonPayment) : undefined,
  };
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Export amortization schedule to CSV format.
 */
export function amortizationToCSV(summary: AmortizationSummary): string {
  const header = 'Period,Due Date,Payment,Principal,Interest,Balance';
  const rows = summary.schedule.map(
    r =>
      `${r.period},${r.dueDate},${r.payment.toFixed(2)},${r.principal.toFixed(2)},${r.interest.toFixed(2)},${r.balance.toFixed(2)}`,
  );
  const footer = [
    '',
    `Totals,,${summary.totalPayment.toFixed(2)},${summary.totalPrincipal.toFixed(2)},${summary.totalInterest.toFixed(2)},`,
  ];
  return [header, ...rows, ...footer].join('\n');
}
